import React, { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, Loader2, CheckCircle, Search, Plus } from 'lucide-react'
import Modal from './Modal'
import { api } from '../lib/api'

interface AddFeedModalProps {
  isOpen: boolean
  onClose: () => void
  collections: Array<{ id: string; title: string }>
}

interface FeedForm {
  name: string
  taxii_url: string
  collection_ids: string[]
  poll_interval_minutes: number
  auth_type: 'none' | 'basic' | 'bearer'
  username: string
  password: string
  bearer_token: string
}

export default function AddFeedModal({ isOpen, onClose, collections }: AddFeedModalProps) {
  const [form, setForm] = useState<FeedForm>({
    name: '',
    taxii_url: '',
    collection_ids: [],
    poll_interval_minutes: 60,
    auth_type: 'none',
    username: '',
    password: '',
    bearer_token: ''
  })
  const [error, setError] = useState('')
  const [validationResult, setValidationResult] = useState<any>(null)
  const [discoveredCollections, setDiscoveredCollections] = useState<any[]>([])
  const [missingCollections, setMissingCollections] = useState<any[]>([])
  const [showCollectionPrompt, setShowCollectionPrompt] = useState(false)

  const queryClient = useQueryClient()

  const validateFeedMutation = useMutation({
    mutationFn: async (feedData: FeedForm) => {
      // Prepare auth config based on auth type
      let auth_config = null
      if (feedData.auth_type === 'basic') {
        auth_config = {
          type: 'basic',
          username: feedData.username,
          password: feedData.password
        }
      } else if (feedData.auth_type === 'bearer') {
        auth_config = {
          type: 'bearer',
          token: feedData.bearer_token
        }
      }

      return api.validateFeed({
        url: feedData.taxii_url,
        auth_type: feedData.auth_type,
        auth_config
      })
    },
    onSuccess: async (result: any) => {
      console.log('🔍 FULL Validation result:', JSON.stringify(result, null, 2))
      setValidationResult(result)
      setError('')

      console.log('📊 Validation valid?', result.valid)
      
      if (result.valid) {
        // If validation succeeded, now discover collections
        console.log('✅ Validation succeeded, now discovering collections...')
        try {
          // Prepare the same data structure as validation
          const discoveryData = {
            url: form.taxii_url,
            auth_type: form.auth_type,
            auth_config: null as any
          }
          
          // Prepare auth config based on auth type (same as validation)
          if (form.auth_type === 'basic') {
            discoveryData.auth_config = {
              type: 'basic',
              username: form.username,
              password: form.password
            }
          } else if (form.auth_type === 'bearer') {
            discoveryData.auth_config = {
              type: 'bearer',
              token: form.bearer_token
            }
          }
          
          console.log('🔗 Sending discovery request with data:', JSON.stringify(discoveryData, null, 2))
          const collectionsResult = await api.discoverCollections(discoveryData)
          console.log('🔍 Collections discovery result:', JSON.stringify(collectionsResult, null, 2))
          
          if (collectionsResult.collections) {
            // Extract discovered collections
            const discovered = collectionsResult.collections.collections || []
            console.log('🔍 Discovered collections:', JSON.stringify(discovered, null, 2))
            console.log('🏠 Existing collections:', JSON.stringify(collections, null, 2))
            setDiscoveredCollections(discovered)

            // Check which collections don't exist in our platform
            const existingCollectionIds = new Set(collections.map(c => c.id))
            const missing = discovered.filter((dc: any) => !existingCollectionIds.has(dc.id) && dc.can_read)
            console.log('❌ Missing collections:', JSON.stringify(missing, null, 2))
            console.log('📋 Existing collection IDs:', Array.from(existingCollectionIds))
            setMissingCollections(missing)
          } else {
            console.log('❌ Collections discovery failed or no collections found')
            setDiscoveredCollections([])
            setMissingCollections([])
          }
        } catch (error) {
          console.log('❌ Collections discovery error:', error)
          setDiscoveredCollections([])
          setMissingCollections([])
        }
      } else {
        console.log('❌ Validation failed')
        setDiscoveredCollections([])
        setMissingCollections([])
      }
    },
    onError: (error: Error) => {
      setError(error.message)
      setValidationResult(null)
      setDiscoveredCollections([])
      setMissingCollections([])
    }
  })

  // Auto-validate TAXII URL when it changes (with debounce)
  useEffect(() => {
    if (!form.taxii_url.trim()) {
      setValidationResult(null)
      setDiscoveredCollections([])
      setMissingCollections([])
      return
    }

    // Basic URL validation
    try {
      new URL(form.taxii_url)
    } catch {
      setValidationResult(null)
      setDiscoveredCollections([])
      setMissingCollections([])
      return
    }

    const timeoutId = setTimeout(() => {
      validateFeedMutation.mutate(form)
    }, 1500) // Debounce for 1.5 seconds

    return () => clearTimeout(timeoutId)
  }, [form.taxii_url, form.auth_type, form.username, form.password, form.bearer_token])

  const createFeedMutation = useMutation({
    mutationFn: async (feedData: FeedForm) => {
      // Prepare auth config based on auth type
      let auth_config = null
      if (feedData.auth_type === 'basic') {
        auth_config = {
          type: 'basic',
          username: feedData.username,
          password: feedData.password
        }
      } else if (feedData.auth_type === 'bearer') {
        auth_config = {
          type: 'bearer',
          token: feedData.bearer_token
        }
      }

      // Create multiple feeds, one for each collection
      // First, ensure all collections exist by creating missing ones
      const results = []
      
      for (const collectionId of feedData.collection_ids) {
        // Check if this collection needs to be created
        const existingCollection = collections.find(c => c.id === collectionId)
        
        if (!existingCollection) {
          // Collection doesn't exist, find it in discovered collections or create a basic one
          const discoveredCollection = discoveredCollections.find(dc => dc.id === collectionId)
          
          if (discoveredCollection) {
            console.log(`Creating missing collection: ${discoveredCollection.title} (${collectionId})`)
            try {
              await api.createCollection({
                id: discoveredCollection.id,
                title: discoveredCollection.title,
                description: discoveredCollection.description || `Discovered from TAXII server: ${discoveredCollection.title}`,
                can_read: discoveredCollection.can_read,
                can_write: discoveredCollection.can_write || false,
                media_types: discoveredCollection.media_types || ['application/stix+json;version=2.1']
              })
              console.log(`Successfully created collection: ${discoveredCollection.title}`)
            } catch (createError) {
              console.error(`Failed to create collection ${discoveredCollection.title}:`, createError)
              throw new Error(`Failed to create collection "${discoveredCollection.title}": ${createError instanceof Error ? createError.message : 'Unknown error'}`)
            }
          } else {
            // Collection not found in discovered collections, create a basic one
            console.log(`Creating basic collection for unknown ID: ${collectionId}`)
            try {
              await api.createCollection({
                id: collectionId,
                title: collectionId,
                description: `Auto-created collection for feed: ${collectionId}`,
                can_read: true,
                can_write: false,
                media_types: ['application/stix+json;version=2.1']
              })
              console.log(`Successfully created basic collection: ${collectionId}`)
            } catch (createError) {
              console.error(`Failed to create basic collection ${collectionId}:`, createError)
              throw new Error(`Failed to create collection "${collectionId}": ${createError instanceof Error ? createError.message : 'Unknown error'}`)
            }
          }
        }
        
        // Now create the feed
        const discoveredCollection = discoveredCollections.find(dc => dc.id === collectionId)
        const collectionTitle = existingCollection?.title || discoveredCollection?.title || collectionId
        
        // Construct the full TAXII collection URL for polling
        // The poller expects taxii_url to be the full collection endpoint
        
        // Always construct the collection URL from the base URL for now
        // This ensures proper polling regardless of discovery data format
        const baseUrl = feedData.taxii_url.replace(/\/$/, '')
        const collectionUrl = `${baseUrl}/api/v1/collections/${collectionId}`
        
        console.log(`🔍 Constructed collection URL: ${collectionUrl} from base: ${feedData.taxii_url}`)
        console.log(`🔍 Collection ID: ${collectionId}`)
        
        const payload = {
          name: feedData.collection_ids.length > 1 ? `${feedData.name} (${collectionTitle})` : feedData.name,
          taxii_url: collectionUrl,
          collection_id: collectionId,
          poll_interval_minutes: feedData.poll_interval_minutes,
          auth_type: feedData.auth_type,
          auth_config: auth_config ? JSON.stringify(auth_config) : null,
          is_active: true
        }
        
        console.log(`Creating feed for collection: ${collectionTitle} (${collectionId})`)
        console.log(`Collection URL for polling: ${collectionUrl}`)
        results.push(await api.createFeed(payload))
      }
      return results
    },
    onSuccess: (results) => {
      // Invalidate both feeds and collections queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: ['feeds'] })
      queryClient.invalidateQueries({ queryKey: ['collections'] })
      
      console.log(`Successfully created ${Array.isArray(results) ? results.length : 1} feed(s)`)
      onClose()
      resetForm()
    },
    onError: (error: Error) => {
      setError(error.message)
    }
  })

  const resetForm = () => {
    setForm({
      name: '',
      taxii_url: '',
      collection_ids: [],
      poll_interval_minutes: 60,
      auth_type: 'none',
      username: '',
      password: '',
      bearer_token: ''
    })
    setError('')
    setValidationResult(null)
    setDiscoveredCollections([])
    setMissingCollections([])
    setShowCollectionPrompt(false)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // Basic validation
    if (!form.name.trim()) {
      setError('Feed name is required')
      return
    }

    if (!form.taxii_url.trim()) {
      setError('TAXII URL is required')
      return
    }

    if (!form.collection_ids || form.collection_ids.length === 0) {
      setError('Please select at least one collection')
      return
    }

    // Validate URL format
    try {
      new URL(form.taxii_url)
    } catch {
      setError('Please enter a valid URL')
      return
    }

    createFeedMutation.mutate(form)
  }

  const handleValidate = () => {
    setError('')
    
    // Basic URL validation
    if (!form.taxii_url.trim()) {
      setError('TAXII URL is required')
      return
    }

    try {
      new URL(form.taxii_url)
    } catch {
      setError('Please enter a valid URL')
      return
    }

    validateFeedMutation.mutate(form)
  }

  const handleCreateSingleCollection = async (collection: any) => {
    try {
      await api.createCollection({
        id: collection.id,
        title: collection.title,
        description: collection.description || `Discovered from TAXII server: ${collection.title}`,
        can_read: collection.can_read,
        can_write: collection.can_write || false,
        media_types: collection.media_types || ['application/stix+json;version=2.1']
      })
      
      // Refresh collections list
      queryClient.invalidateQueries({ queryKey: ['collections'] })
      
      // Remove from missing collections and add to selected collections
      setMissingCollections(prev => prev.filter(mc => mc.id !== collection.id))
      setForm(prev => ({ 
        ...prev, 
        collection_ids: [...prev.collection_ids, collection.id]
      }))
      
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to create collection')
    }
  }

  const handleCreateMissingCollections = async () => {
    try {
      // Create missing collections
      for (const collection of missingCollections) {
        await api.createCollection({
          id: collection.id,
          title: collection.title,
          description: collection.description || `Discovered from TAXII server: ${collection.title}`,
          can_read: collection.can_read,
          can_write: collection.can_write || false,
          media_types: collection.media_types || ['application/stix+json;version=2.1']
        })
      }
      
      // Refresh collections list
      queryClient.invalidateQueries({ queryKey: ['collections'] })
      setShowCollectionPrompt(false)
      setMissingCollections([])
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to create collections')
    }
  }

  const handleClose = () => {
    if (!createFeedMutation.isPending && !validateFeedMutation.isPending) {
      resetForm()
      onClose()
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add Threat Feed" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              Feed Name *
            </label>
            <input
              type="text"
              id="name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="e.g., MISP Community Feed"
              required
            />
          </div>

          <div className="md:col-span-2">
            <label htmlFor="taxii_url" className="block text-sm font-medium text-gray-700">
              TAXII URL *
            </label>
            <div className="relative mt-1">
              <input
                type="url"
                id="taxii_url"
                value={form.taxii_url}
                onChange={(e) => setForm({ ...form, taxii_url: e.target.value })}
                className="block w-full border border-gray-300 rounded-md px-3 py-2 pr-10 focus:ring-primary-500 focus:border-primary-500"
                placeholder="https://demo-taxii-server.unemployed.workers.dev"
                required
              />
              {validateFeedMutation.isPending && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                </div>
              )}
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Collections will be discovered automatically when you enter a valid TAXII URL
            </p>
          </div>

          {/* Validation Results */}
          {validationResult && (
            <div className="md:col-span-2">
              {validationResult.valid ? (
                <div className="rounded-md bg-green-50 p-4">
                  <div className="flex">
                    <CheckCircle className="h-5 w-5 text-green-400" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-green-800">Valid TAXII Server</h3>
                      <div className="mt-2 text-sm text-green-700">
                        <p>Successfully discovered {validationResult.metadata?.availableCollections || 0} readable collections</p>
                        {validationResult.metadata?.serverTitle && (
                          <p className="mt-1"><strong>Server:</strong> {validationResult.metadata.serverTitle}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Validation Failed</h3>
                      <div className="mt-2 text-sm text-red-700">
                        <ul className="list-disc list-inside">
                          {validationResult.errors?.map((error: string, i: number) => (
                            <li key={i}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {validationResult.warnings && validationResult.warnings.length > 0 && (
                <div className="rounded-md bg-yellow-50 p-4 mt-4">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-yellow-400" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">Warnings</h3>
                      <div className="mt-2 text-sm text-yellow-700">
                        <ul className="list-disc list-inside">
                          {validationResult.warnings.map((warning: string, i: number) => (
                            <li key={i}>{warning}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Missing Collections Prompt */}
          {showCollectionPrompt && missingCollections.length > 0 && (
            <div className="md:col-span-2">
              <div className="rounded-md bg-blue-50 p-4">
                <div className="flex">
                  <Plus className="h-5 w-5 text-blue-400" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">New Collections Discovered</h3>
                    <div className="mt-2 text-sm text-blue-700">
                      <p>Found {missingCollections.length} collections that don't exist in your platform:</p>
                      <ul className="mt-2 list-disc list-inside">
                        {missingCollections.map((collection: any) => (
                          <li key={collection.id}>
                            <strong>{collection.title}</strong> ({collection.id})
                            {collection.description && ` - ${collection.description}`}
                          </li>
                        ))}
                      </ul>
                      <div className="mt-3">
                        <button
                          type="button"
                          onClick={handleCreateMissingCollections}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Create These Collections
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowCollectionPrompt(false)}
                          className="ml-3 inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        >
                          Skip
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Target Collections * (Select multiple)
            </label>
            
            {/* Existing Collections */}
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-600 mb-2">📁 Existing Collections</h4>
              {collections.length > 0 ? (
                <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-200 rounded-md p-2">
                  {collections.map((collection) => (
                    <label key={collection.id} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.collection_ids.includes(collection.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setForm(prev => ({
                              ...prev,
                              collection_ids: [...prev.collection_ids, collection.id]
                            }))
                          } else {
                            setForm(prev => ({
                              ...prev,
                              collection_ids: prev.collection_ids.filter(id => id !== collection.id)
                            }))
                          }
                        }}
                        className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-900">{collection.title}</span>
                      <span className="text-xs text-gray-500">({collection.id})</span>
                    </label>
                  ))}
                </div>
              ) : (
                <div className="border border-gray-200 rounded-md p-4 text-center bg-gray-50">
                  <p className="text-sm text-gray-500">No existing collections found</p>
                  <p className="text-xs text-gray-400 mt-1">Enter a TAXII URL above to discover collections</p>
                </div>
              )}
            </div>
            
            {/* Discovered Collections that match existing */}
            {discoveredCollections.length > 0 && discoveredCollections.some(dc => dc.can_read && collections.some(c => c.id === dc.id)) && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-600 mb-2">🔍 Discovered Collections (Already in Platform)</h4>
                <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-200 rounded-md p-2">
                  {discoveredCollections
                    .filter(dc => dc.can_read && collections.some(c => c.id === dc.id))
                    .map((collection) => (
                      <label key={`discovered-${collection.id}`} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={form.collection_ids.includes(collection.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setForm(prev => ({
                                ...prev,
                                collection_ids: [...prev.collection_ids, collection.id]
                              }))
                            } else {
                              setForm(prev => ({
                                ...prev,
                                collection_ids: prev.collection_ids.filter(id => id !== collection.id)
                              }))
                            }
                          }}
                          className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className="text-sm text-green-700">✅ {collection.title}</span>
                        <span className="text-xs text-gray-500">(matches existing)</span>
                        <span className="text-xs text-gray-400">({collection.id})</span>
                      </label>
                    ))}
                </div>
              </div>
            )}
            
            {/* Missing Collections */}
            {missingCollections.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-600">➕ New Collections Discovered</h4>
                  <button
                    type="button"
                    onClick={async () => {
                      // Auto-create all missing collections
                      for (const collection of missingCollections) {
                        await handleCreateSingleCollection(collection)
                      }
                    }}
                    className="px-3 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                  >
                    Create All ({missingCollections.length})
                  </button>
                </div>
                <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-200 rounded-md p-3 bg-blue-50">
                  {missingCollections.map((collection) => (
                    <div key={`create-${collection.id}`} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-blue-800">{collection.title}</span>
                            <span className="text-xs text-gray-600">({collection.id})</span>
                          </div>
                          {collection.description && (
                            <p className="text-xs text-gray-600 mt-1">{collection.description}</p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleCreateSingleCollection(collection)}
                          className="px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 border border-blue-300 hover:border-blue-400 rounded transition-colors"
                        >
                          Create & Select
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <div className="flex items-start">
                    <svg className="h-4 w-4 text-yellow-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                    <div className="ml-2">
                      <p className="text-sm font-medium text-yellow-800">Collections need to be created first</p>
                      <p className="text-xs text-yellow-700 mt-1">
                        These collections don't exist in your platform yet. Create them individually or use "Create All" to create and select all collections automatically.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div className={`mt-3 p-2 rounded-md ${form.collection_ids.length > 0 ? 'bg-green-50' : 'bg-yellow-50'}`}>
              <p className={`text-sm ${form.collection_ids.length > 0 ? 'text-green-800' : 'text-yellow-800'}`}>
                {form.collection_ids.length > 0 ? (
                  <>
                    Selected {form.collection_ids.length} collection{form.collection_ids.length !== 1 ? 's' : ''}
                    {form.collection_ids.length > 1 && ' (will create separate feeds for each)'}
                  </>
                ) : (
                  'No collections selected - you must select at least one collection to create a feed'
                )}
              </p>
            </div>
          </div>

          <div className="md:col-span-2">
            <label htmlFor="poll_interval" className="block text-sm font-medium text-gray-700">
              Poll Interval (minutes)
            </label>
            <input
              type="number"
              id="poll_interval"
              min="1"
              max="1440"
              value={form.poll_interval_minutes}
              onChange={(e) => setForm({ ...form, poll_interval_minutes: parseInt(e.target.value) || 60 })}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div className="md:col-span-2">
            <label htmlFor="auth_type" className="block text-sm font-medium text-gray-700">
              Authentication
            </label>
            <select
              id="auth_type"
              value={form.auth_type}
              onChange={(e) => setForm({ ...form, auth_type: e.target.value as any })}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="none">No Authentication</option>
              <option value="basic">Basic Auth (Username/Password)</option>
              <option value="bearer">Bearer Token</option>
            </select>
          </div>

          {form.auth_type === 'basic' && (
            <>
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                  Username
                </label>
                <input
                  type="text"
                  id="username"
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </>
          )}

          {form.auth_type === 'bearer' && (
            <div className="md:col-span-2">
              <label htmlFor="bearer_token" className="block text-sm font-medium text-gray-700">
                Bearer Token
              </label>
              <input
                type="password"
                id="bearer_token"
                value={form.bearer_token}
                onChange={(e) => setForm({ ...form, bearer_token: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            disabled={createFeedMutation.isPending || validateFeedMutation.isPending}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createFeedMutation.isPending || validateFeedMutation.isPending || !validationResult?.valid}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center"
          >
            {createFeedMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {createFeedMutation.isPending ? 'Adding Feed...' : 'Add Feed'}
          </button>
        </div>
      </form>
    </Modal>
  )
}